import { supabase } from "./supabase";
import {
  fetchLeavesForGrade,
  fetchCurricula,
  fetchSubjectsForCurriculum,
  fetchCoursesForSubject,
  fetchLeavesForCourse,
} from "./curriculumApiService";

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

/**
 * Build a sample test-generator request payload from the curriculum API.
 *
 * Picks a random substandard for the chosen curriculum/grade/subject,
 * defaults the type/difficulty/locale, and returns a payload shaped per the
 * RequestSchema in @trilogy-innovations/content-renderers (see
 * generator-api-interface for the contract).
 *
 * @param {Object} filters
 * @param {string} filters.curriculum - DB curriculum name (e.g. "common core", "ap")
 * @param {string} filters.grade - Grade label (e.g. "3", "K", or AP unit number)
 * @param {string} filters.subject - DB subject (e.g. "math", "ela", "apush")
 * @param {string} [filters.type] - Content type (default "mcq")
 * @param {string} [filters.difficulty] - Difficulty (default "medium")
 * @param {string} [filters.locale] - Locale (default "en-US")
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export const fetchSampleRequest = async (filters = {}) => {
  try {
    const { curriculum, grade, subject } = filters;
    if (!curriculum || !grade || !subject) {
      return {
        data: null,
        error: new Error(
          "curriculum, grade, and subject are all required to build a test request"
        ),
      };
    }

    const { data: leaves, error } = await fetchLeavesForGrade(
      curriculum,
      subject,
      grade
    );
    if (error) {
      throw error;
    }
    if (!leaves || leaves.length === 0) {
      return { data: null, error: null };
    }

    const leaf = leaves[Math.floor(Math.random() * leaves.length)];

    const requestPayload = {
      curriculum,
      subject: subject.toLowerCase(),
      grade,
      type: filters.type || "mcq",
      difficulty: filters.difficulty || "medium",
      locale: filters.locale || "en-US",
      skills: {
        substandard_id: leaf.leafId,
        substandard_description: leaf.description || "",
      },
    };

    if (leaf.lessonName) {
      requestPayload.skills.lesson_title = leaf.lessonName;
    }

    return { data: requestPayload, error: null };
  } catch (error) {
    console.error("Error building sample request from curriculum API:", error);
    return { data: null, error };
  }
};

/**
 * Build a sample test-generator request by walking the curriculum API directly
 * (no DB benchmark sets, no hardcoded name maps). Any filter left empty is
 * resolved to a random pick from the matching API response, so the preview
 * always produces a concrete payload that respects whatever the user did
 * choose.
 *
 * @param {Object} filters - { curriculum, subject, course, type, difficulty, locale }
 *   Each field is optional; "" or undefined means "Any".
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
const TYPE_OPTIONS = [
  "mcq",
  "msq",
  "fill-in",
  "match",
  "article",
  "mcq_set",
  "saq",
  "leq",
  "dbq",
  "frq_short",
  "frq_long",
];
const DIFFICULTY_OPTIONS = ["easy", "medium", "hard"];

export const fetchSampleRequestFromApi = async (filters = {}) => {
  try {
    let { curriculum, subject, course } = filters;

    if (!curriculum) {
      const { data: curricula, error: cErr } = await fetchCurricula();
      if (cErr) {
        throw cErr;
      }
      if (curricula.length === 0) {
        return { data: null, error: new Error("No curricula available") };
      }
      curriculum = pickRandom(curricula).id;
    }

    if (!subject) {
      const { data: subjects, error: sErr } =
        await fetchSubjectsForCurriculum(curriculum);
      if (sErr) {
        throw sErr;
      }
      if (subjects.length === 0) {
        return {
          data: null,
          error: new Error(`No subjects available for ${curriculum}`),
        };
      }
      subject = pickRandom(subjects);
    }

    if (!course) {
      const { data: courses, error: coErr } = await fetchCoursesForSubject(
        curriculum,
        subject
      );
      if (coErr) {
        throw coErr;
      }
      if (courses.length === 0) {
        return {
          data: null,
          error: new Error(
            `No courses available for ${curriculum} / ${subject}`
          ),
        };
      }
      course = pickRandom(courses);
    }

    const { data: leaves, error: lErr } = await fetchLeavesForCourse(
      curriculum,
      subject,
      course
    );
    if (lErr) {
      throw lErr;
    }
    if (!leaves || leaves.length === 0) {
      return {
        data: null,
        error: new Error(
          `No substandards under ${curriculum} / ${subject} / ${course}`
        ),
      };
    }

    const leaf = pickRandom(leaves);
    const requestPayload = {
      curriculum,
      subject,
      grade: course,
      type: filters.type || pickRandom(TYPE_OPTIONS),
      difficulty: filters.difficulty || pickRandom(DIFFICULTY_OPTIONS),
      locale: filters.locale || "en-US",
      skills: {
        substandard_id: leaf.leafId,
        substandard_description: leaf.description || "",
      },
    };

    return {
      data: requestPayload,
      error: null,
      resolved: { curriculum, subject, course },
    };
  } catch (error) {
    console.error("Error building sample request from curriculum API:", error);
    return { data: null, error };
  }
};

/**
 * Test a generator endpoint by sending a sample request via the test-generator edge function.
 * Returns structured diagnostic results including reachability, auth, format, and schema checks.
 * @param {string} generatorId - Generator ID to test
 * @param {Object} [requestPayload] - Optional request payload to send (if not provided, edge function picks one)
 * @returns {Promise<{data: Object, error: Object}>}
 */
// Timeout for the test-generator edge function call (5 min + 30s buffer for edge function overhead)
const TEST_GENERATOR_TIMEOUT_MS = 330000;

export const testGenerator = async (generatorId, requestPayload = null) => {
  try {
    // Get current session for auth token
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error("No active session. Please log in again.");
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TEST_GENERATOR_TIMEOUT_MS
    );

    const body = { generatorId };
    if (requestPayload) {
      body.requestPayload = requestPayload;
    }

    const supabaseUrl = supabase.supabaseUrl;
    const response = await fetch(`${supabaseUrl}/functions/v1/test-generator`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        message = errorData.error || message;
      } catch {
        // Response wasn't JSON — use the default message
      }
      throw new Error(message);
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    if (error.name === "AbortError") {
      const timeoutError = new Error(
        "Request timed out. Your generator may take too long to respond."
      );
      return { data: null, error: timeoutError };
    }
    console.error("Error testing generator:", error);
    return { data: null, error };
  }
};

/**
 * Fetch all generators from the database
 * Note: auth_credential is excluded for security - it should never be exposed to the frontend
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchGenerators = async () => {
  try {
    const { data, error } = await supabase
      .from("bm_generators")
      .select(
        "id, name, endpoint_url, auth_type, user_id, created_at, concurrency_limit"
      )
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching generators:", error);
    return { data: null, error };
  }
};

/**
 * Create a new generator
 * @param {Object} generator - Generator data
 * @param {string} generator.name - Generator name
 * @param {string} generator.endpoint_url - Generator endpoint URL
 * @param {string} generator.auth_type - Authentication type ('none', 'bearer', 'x-api-key')
 * @param {string} [generator.auth_credential] - Optional auth credential (token/key)
 * @param {number} [generator.concurrency_limit] - Optional concurrency limit (default: 20)
 * @param {string} userId - ID of the user creating the generator
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const createGenerator = async (generator, userId) => {
  try {
    const { data, error } = await supabase
      .from("bm_generators")
      .insert([
        {
          name: generator.name,
          endpoint_url: generator.endpoint_url,
          auth_type: generator.auth_type || "none",
          auth_credential:
            generator.auth_type === "none"
              ? null
              : generator.auth_credential || null,
          concurrency_limit: generator.concurrency_limit || 20,
          user_id: userId,
        },
      ])
      .select(
        "id, name, endpoint_url, auth_type, user_id, created_at, concurrency_limit"
      )
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error creating generator:", error);
    return { data: null, error };
  }
};

/**
 * Update an existing generator
 * @param {string} id - Generator ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const updateGenerator = async (id, updates) => {
  try {
    const updateData = {};

    if (updates.name !== undefined) {
      updateData.name = updates.name;
    }
    if (updates.endpoint_url !== undefined) {
      updateData.endpoint_url = updates.endpoint_url;
    }
    if (updates.auth_type !== undefined) {
      updateData.auth_type = updates.auth_type;
      // If changing to 'none', clear the credential
      if (updates.auth_type === "none") {
        updateData.auth_credential = null;
      }
    }
    // Only update auth_credential if a new value is provided (not empty string)
    if (updates.auth_credential && updates.auth_credential.trim() !== "") {
      updateData.auth_credential = updates.auth_credential;
    }
    // Update concurrency_limit if provided
    if (updates.concurrency_limit !== undefined) {
      updateData.concurrency_limit = updates.concurrency_limit;
    }

    const { data, error } = await supabase
      .from("bm_generators")
      .update(updateData)
      .eq("id", id)
      .select(
        "id, name, endpoint_url, auth_type, user_id, created_at, concurrency_limit"
      )
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error updating generator:", error);
    return { data: null, error };
  }
};

/**
 * Delete a generator
 * @param {string} id - Generator ID
 * @returns {Promise<{error: Object}>}
 */
export const deleteGenerator = async (id) => {
  try {
    const { error } = await supabase
      .from("bm_generators")
      .delete()
      .eq("id", id);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    console.error("Error deleting generator:", error);
    return { error };
  }
};

/**
 * Subscribe to real-time changes in the generators table
 * @param {Function} callback - Function to call when data changes
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToGenerators = (callback) => {
  const subscription = supabase
    .channel("bm_generators_changes")
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "bm_generators",
      },
      (payload) => {
        callback(payload);
      }
    )
    .subscribe();

  return subscription;
};
